import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { authService } from "../../context/AuthService";
import GlowingOrb from "../../components/ui/GlowingOrb";
import DOMAIN_CONFIG from "../../config/domains";

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, initialAuthCheckDone } = useAuth();
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState(null);
  const [authCompleted, setAuthCompleted] = useState(false);

  const authToken = searchParams.get("authToken");
  const nextPath = searchParams.get("next") || "/";

  useEffect(() => {
    const handleAuth = async () => {
      // In development, user might already be authenticated on same domain
      if (DOMAIN_CONFIG.isDevelopment() && user && initialAuthCheckDone) {
        navigate(nextPath, { replace: true });
        return;
      }

      if (!authToken) {
        if (user) {
          navigate(nextPath, { replace: true });
        } else {
          DOMAIN_CONFIG.redirectToAuth("/login");
        }
        return;
      }

      setStatus("exchanging");

      try {
        const exchangeResult = await authService.exchangeTokenForCustomToken(authToken);

        if (!exchangeResult.success) {
          console.error("Token exchange failed:", exchangeResult.message);
          // In development, if exchange fails but user is already authenticated, just navigate
          if (DOMAIN_CONFIG.isDevelopment() && user) {
            navigate(nextPath, { replace: true });
            return;
          }
          setError("Authentication failed. Please try logging in again.");
          setStatus("error");
          return;
        }

        setStatus("signing-in");

        const signInResult = await authService.signInWithToken(exchangeResult.customToken);

        if (!signInResult.success) {
          console.error("Sign in failed:", signInResult.message);
          // In development, if sign-in fails but user is already authenticated, just navigate
          if (DOMAIN_CONFIG.isDevelopment() && user) {
            navigate(nextPath, { replace: true });
            return;
          }
          setError("Sign in failed. Please try again.");
          setStatus("error");
          return;
        }

        setStatus("waiting");
        setAuthCompleted(true);
      } catch (err) {
        console.error("Auth callback error:", err);
        // In development, if there's an error but user is authenticated, just navigate
        if (DOMAIN_CONFIG.isDevelopment() && user) {
          navigate(nextPath, { replace: true });
          return;
        }
        setError("An unexpected error occurred. Please try again.");
        setStatus("error");
      }
    };

    handleAuth();
  }, [authToken, user, initialAuthCheckDone, navigate, nextPath]);

  useEffect(() => {
    if (authCompleted && initialAuthCheckDone && user) {
      navigate(nextPath, { replace: true });
    }
  }, [authCompleted, initialAuthCheckDone, user, nextPath, navigate]);

  useEffect(() => {
    if (status === "error") {
      const timer = setTimeout(() => {
        DOMAIN_CONFIG.redirectToAuth("/login");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  if (status === "error") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <div className="text-red-500 text-lg mb-4">{error}</div>
        <div className="text-gray-500">Redirecting to login...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white">
      <GlowingOrb size="md" />
      <div className="mt-4 text-gray-600">
        {status === "exchanging" && "Verifying credentials..."}
        {status === "signing-in" && "Signing you in..."}
        {status === "waiting" && "Almost there..."}
        {status === "processing" && "Processing..."}
      </div>
    </div>
  );
};

export default AuthCallback;
